import type { Activity, Paginated } from '../types';
import type { DashboardAttentionItem } from '../api/dashboard';
import type { CareIssue } from '../api/customerCare';

export type DayScope = 'mine' | 'team' | 'unassigned';
export interface DueWork { activity: Activity; date: string; day: string; kind: 'touchpoint' | 'next_action' }
export const manualActivityTypes = ['task', 'call', 'cold_call', 'meeting', 'note', 'whatsapp', 'visit'] as const;

export function matchesOwnership(assignee: number | null | undefined, scope: DayScope, userId?: number): boolean {
  if (scope === 'team') return true;
  // Missing ownership metadata is not evidence that something is unassigned.
  if (scope === 'unassigned') return assignee === null;
  return userId !== undefined && assignee === userId;
}

export function dayInIndia(value: string, calendarDate = false): string | undefined {
  if (calendarDate || /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const day = value.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return undefined;
    const parsed = new Date(`${day}T00:00:00Z`);
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === day ? day : undefined;
  }
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return undefined;
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(parsed);
}

export function dueWork(rows: Activity[], today: string, scope: DayScope, userId?: number): DueWork[] {
  const output: DueWork[] = [];
  for (const activity of rows) {
    const emailReminder=activity.type==='email' && activity.status==='completed' && !!activity.next_action_date;
    if ((!manualActivityTypes.some(type => type === activity.type) && !emailReminder) || !['scheduled', 'completed'].includes(activity.status) || !matchesOwnership(activity.assignee_user_id, scope, userId)) continue;
    // Match the server's single-row precedence exactly. An old reminder cannot
    // make a future appointment overdue; explicit no-follow-up hides reminders.
    const appointment = activity.status === 'scheduled' ? activity.scheduled_at || activity.due_at : null;
    const date = appointment || (!activity.no_followup ? activity.next_action_date : null);
    if (!date) continue;
    const kind = appointment ? 'touchpoint' : 'next_action';
    const day = dayInIndia(date, kind === 'next_action');
    if (day && day <= today) output.push({ activity, date, day, kind });
  }
  return output.sort((a, b) => a.day.localeCompare(b.day) || a.date.localeCompare(b.date) || a.activity.id - b.activity.id);
}

// Retained for existing callers; new UI also needs to know which action is due.
export function dueActivities(rows: Activity[], today: string, scope: DayScope, userId?: number): Activity[] {
  return dueWork(rows, today, scope, userId).map(work => work.activity);
}
export function canCompleteTouchpoint(work: DueWork): boolean {
  return work.kind === 'touchpoint' && work.activity.status === 'scheduled' && ['task', 'call', 'cold_call', 'meeting', 'visit'].includes(work.activity.type);
}
export function touchpointCompletion(activity: Activity, outcome: string): { status: 'completed'; outcome: string } {
  if (activity.status !== 'scheduled' || !['task', 'call', 'cold_call', 'meeting'].includes(activity.type)) throw new Error('This touchpoint is no longer scheduled. Open it to review the current action.');
  if (!outcome.trim()) throw new Error('Describe the outcome before completing this touchpoint.');
  return { status: 'completed', outcome: outcome.trim() };
}
export function quietDealReminders(items: DashboardAttentionItem[]): DashboardAttentionItem[] {
  return items.filter((item, index) => item.type === 'deal_stale' && item.target_view === 'deals' && item.target_id > 0 && items.findIndex(other => other.type === 'deal_stale' && other.target_view === 'deals' && other.target_id === item.target_id) === index);
}
export function stalledCaseReminders(items: CareIssue[]): CareIssue[] {
  return items.filter(item => item.stalled && item.status !== 'resolved');
}

export function todaysMeetings(rows: Activity[], today: string, mode: 'booked' | 'assigned', user: { id: number; email: string }): Activity[] {
  return rows.filter(row => row.type === 'meeting' && row.status === 'scheduled' && !!row.scheduled_at
    && dayInIndia(row.scheduled_at) === today
    && (mode === 'booked' ? !!user.email.trim() && row.created_by?.trim().toLowerCase() === user.email.trim().toLowerCase() : row.assignee_user_id === user.id))
    .sort((a, b) => Date.parse(a.scheduled_at!) - Date.parse(b.scheduled_at!) || a.id - b.id);
}

export function mayCompleteOwnTouchpoint(work: DueWork, userId?: number): boolean {
  // Booking a meeting is not ownership of the attendee's work. Team triage
  // always opens the record; inline completion is only for assigned work.
  return userId !== undefined && work.activity.assignee_user_id === userId && canCompleteTouchpoint(work);
}

// Counts for personal queues require all pages, not a filtered first page.
// Refuse unusually large/inconsistent responses instead of silently presenting a partial count.
export async function readQueuePages<T extends { id: number }>(request: (page: number) => Promise<Paginated<T>>, maxPages = 20): Promise<T[]> {
  const rows = new Map<number, T>();
  for (let page = 1; page <= maxPages; page++) {
    const result = await request(page);
    if (result.page !== page || !Number.isInteger(result.per_page) || result.per_page < 1 || !Number.isInteger(result.count) || result.count < 0) throw new Error('Unable to read the full queue. Open Touchpoints or retry.');
    for (const row of result.results) rows.set(row.id, row);
    if (page * result.per_page >= result.count) {
      if (rows.size !== result.count) throw new Error('The queue changed while loading. Please retry.');
      return [...rows.values()];
    }
    if (!result.results.length) throw new Error('The queue changed while loading. Please retry.');
  }
  throw new Error('This queue is too large for the home view. Open Touchpoints for the full list.');
}

// ── Presentation helpers for the role homes ────────────────────────────────
export function timeInIndia(value: string | null | undefined): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return '';
  return new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit' }).format(parsed);
}
export function greetingInIndia(now: Date): string {
  const hour = Number(new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', hour: '2-digit', hourCycle: 'h23' }).format(now));
  return hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
}
// Whole Indian calendar days between a timestamp and today; null when unknown.
export function daysSinceInIndia(value: string | null | undefined, today: string): number | null {
  const day = value ? dayInIndia(value) : undefined;
  if (!day) return null;
  return Math.max(0, Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${day}T00:00:00Z`)) / 86400000));
}
export function minutesUntil(value: string | null | undefined, now: Date): number | null {
  if (!value) return null;
  const at = Date.parse(value);
  return Number.isFinite(at) ? Math.round((at - now.getTime()) / 60000) : null;
}
export function untilLabel(minutes: number | null): string {
  if (minutes === null) return '';
  if (minutes <= 0) return 'now';
  if (minutes < 60) return `in ${minutes}m`;
  const hours = Math.floor(minutes / 60), rest = minutes % 60;
  return rest ? `in ${hours}h ${rest}m` : `in ${hours}h`;
}
// Today's assigned meetings/calls with a time, in order — the sales agenda.
export function todaysAgenda(rows: Activity[], today: string, userId?: number): Activity[] {
  if (userId === undefined) return [];
  return rows.filter(row => ['meeting', 'call', 'cold_call', 'visit'].includes(row.type) && row.assignee_user_id === userId
    && !!row.scheduled_at && dayInIndia(row.scheduled_at) === today && !['cancelled', 'no_show'].includes(row.status))
    .sort((a, b) => Date.parse(a.scheduled_at!) - Date.parse(b.scheduled_at!) || a.id - b.id);
}
// The next still-scheduled agenda item that has not started more than 30 minutes ago.
export function nextUp(agenda: Activity[], now: Date): Activity | null {
  return agenda.find(row => row.status === 'scheduled' && Date.parse(row.scheduled_at!) >= now.getTime() - 30 * 60000) ?? null;
}
// Progress for the day ring: assigned touchpoints completed today (by their
// last update, India time) against those plus what is still due today/overdue.
export function dayProgress(rows: Activity[], due: DueWork[], today: string, userId?: number): { done: number; total: number; pct: number } {
  const done = userId === undefined ? 0 : rows.filter(row => row.assignee_user_id === userId && row.status === 'completed'
    && manualActivityTypes.some(type => type === row.type) && row.type !== 'note' && dayInIndia(row.updated_at) === today).length;
  const total = done + due.length;
  return { done, total, pct: total ? Math.round(done / total * 100) : 0 };
}
